import React from 'react';

import {Icon, Skeleton, Text} from '@albert-io/atomic';

import {SortableHeaderCell} from './SortableHeaderCell';
import type {SortDir} from './useClientSort';

const SKELETON_ROW_KEYS = ['s1', 's2', 's3', 's4', 's5'];

export interface RosterColumn<Row> {
  /** Stable column id; for sortable columns it is also the sort key passed to onSort/useClientSort. */
  key: string;
  /** Header label; a sortable column renders a SortableHeaderCell with it. */
  label?: string;
  sortable?: boolean;
  /** Classes for the non-sortable <th> (e.g. a fixed width for the actions column). */
  headerClassName?: string;
  /** Visually-hidden header text for icon-only columns (e.g. "Actions"). */
  srOnlyHeader?: string;
  /** Classes for each body <td>. */
  cellClassName?: string;
  render: (row: Row) => React.ReactNode;
}

interface Props<Row> {
  ariaLabel: string;
  columns: RosterColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  sortKey: string;
  sortDir: SortDir;
  onSort: (key: string) => void;
  noResultsMessage: string;
  /** First-page load (initial or after a search/sort change): show skeleton rows in place of the body. */
  isLoading?: boolean;
  /** More pages exist — keep the load-on-scroll sentinel active. */
  hasNextPage?: boolean;
  /** The next page is loading — show the load-more spinner. */
  isLoadingMore?: boolean;
  /** Load the next page (invoked automatically as the sentinel nears the viewport). */
  onShowMore?: () => void;
}

/**
 * Shared roster table for the People tab's three lists (enrolled / invited / co-teachers): the table
 * chrome, sortable headers (SortableHeaderCell), the no-search-results message, and (for the
 * server-paged lists) a first-page loading skeleton and load-on-scroll pagination. Each column carries
 * its own header/cell classes and a render fn, so every list keeps its exact markup.
 */
export function RosterTable<Row>({
  ariaLabel,
  columns,
  rows,
  getRowKey,
  sortKey,
  sortDir,
  onSort,
  noResultsMessage,
  isLoading = false,
  hasNextPage = false,
  isLoadingMore = false,
  onShowMore
}: Props<Row>) {
  // A refetch (search/sort) keeps the loaded rows on screen; only a cold first page shows skeletons.
  const showSkeleton = isLoading && rows.length === 0;

  // Load-on-scroll (mirrors StudentHomepage/Assignments): the observer loads the next page as the
  // sentinel nears the viewport. Callback + guard via refs so it subscribes once; loadMore self-guards.
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const onShowMoreRef = React.useRef(onShowMore);
  const canLoadMoreRef = React.useRef(false);
  React.useEffect(() => {
    onShowMoreRef.current = onShowMore;
    canLoadMoreRef.current = Boolean(hasNextPage && !isLoading && !isLoadingMore && onShowMore);
  });
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[entries.length - 1]?.isIntersecting && canLoadMoreRef.current) {
          onShowMoreRef.current?.();
        }
      },
      {rootMargin: '0px 0px 200px 0px', threshold: 0}
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      {/* Skeleton rows are aria-hidden (Skeleton atom), so announce the load for screen readers. */}
      {showSkeleton && (
        <span className='sr-only' role='status'>
          Loading {ariaLabel.toLowerCase()}…
        </span>
      )}
      <table
        aria-busy={showSkeleton || undefined}
        aria-label={ariaLabel}
        className='w-full border-collapse'
      >
        <thead>
          <tr className='border-border-primary border-b'>
            {columns.map((column) =>
              column.sortable && column.label ? (
                <SortableHeaderCell
                  activeKey={sortKey}
                  dir={sortDir}
                  key={column.key}
                  label={column.label}
                  onSort={onSort}
                  sortKey={column.key}
                />
              ) : (
                <th className={column.headerClassName} key={column.key}>
                  {column.srOnlyHeader ? (
                    <span className='sr-only'>{column.srOnlyHeader}</span>
                  ) : null}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {showSkeleton
            ? SKELETON_ROW_KEYS.map((key) => (
                <tr className='border-border-subtle border-b' key={key}>
                  <td className='px-x2 py-x1-half' colSpan={columns.length}>
                    <Skeleton className='h-x2 w-full rounded-full' />
                  </td>
                </tr>
              ))
            : rows.map((row) => (
                <tr className='border-border-subtle border-b' key={getRowKey(row)}>
                  {columns.map((column) => (
                    <td className={column.cellClassName} key={column.key}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
      {!showSkeleton && rows.length === 0 && (
        <Text as='p' className='py-x4 text-center' color='secondary'>
          {noResultsMessage}
        </Text>
      )}
      {isLoadingMore && (
        <div className='py-x3 flex justify-center'>
          <span className='sr-only' role='status'>
            Loading more {ariaLabel.toLowerCase()}…
          </span>
          <Icon icon='spinner' spin />
        </div>
      )}
      {/* Load-on-scroll sentinel; the observer above loads the next page as this nears the viewport. */}
      <div aria-hidden className='h-px' ref={sentinelRef} />
    </div>
  );
}
